import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText, AlertTriangle, CheckCircle2, Clock,
  Send, RefreshCw, AlertCircle, TrendingUp, Plus,
  DollarSign, Calendar, ChevronRight, Sparkles,
  Target, BarChart3, Trash2
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { formatCurrency, formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

async function listAppeals(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.priority) params.append('priority', filters.priority);
  params.append('limit', '100');

  const response = await fetch(`${API_URL}/api/appeals/?${params.toString()}`);
  if (!response.ok) throw new Error('Failed to fetch appeals');
  return response.json();
}

async function getAppealsQueueSummary() {
  const response = await fetch(`${API_URL}/api/appeals/queue/summary`);
  if (!response.ok) throw new Error('Failed to fetch queue summary');
  return response.json();
}

async function getAppealsAnalytics() {
  const response = await fetch(`${API_URL}/api/appeals/analytics`);
  if (!response.ok) throw new Error('Failed to fetch analytics');
  return response.json();
}

function getUrgencyClass(urgency) {
  switch (urgency?.toUpperCase()) {
    case 'OVERDUE': return 'bg-red-600 text-white';
    case 'CRITICAL': return 'bg-red-500 text-white';
    case 'HIGH': return 'bg-orange-500 text-white';
    case 'MEDIUM': return 'bg-yellow-500 text-black';
    case 'NORMAL': return 'bg-blue-500 text-white';
    default: return 'bg-green-500 text-white';
  }
}

function getStatusBadge(status) {
  const statusMap = {
    'DRAFT': { color: 'bg-gray-100 text-gray-800', icon: FileText },
    'PENDING_REVIEW': { color: 'bg-blue-100 text-blue-800', icon: Clock },
    'APPROVED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    'SUBMITTED': { color: 'bg-purple-100 text-purple-800', icon: Send },
    'ACCEPTED': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
    'REJECTED': { color: 'bg-red-100 text-red-800', icon: AlertTriangle }
  };

  const config = statusMap[status] || statusMap['DRAFT'];
  const Icon = config.icon;

  return (
    <Badge className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {status?.replace('_', ' ')}
    </Badge>
  );
}

function UpcomingDeadlines({ deadlines }) {
  if (!deadlines || deadlines.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">No upcoming deadlines</p>;
  }

  return (
    <div className="space-y-2">
      {deadlines.slice(0, 5).map((item, idx) => (
        <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-mono">{item.claim_id?.slice(-8)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm">{formatCurrency(item.amount)}</span>
            <Badge className={item.days_remaining <= 3 ? 'bg-red-500 text-white' : item.days_remaining <= 7 ? 'bg-orange-500 text-white' : 'bg-yellow-500 text-black'}>
              {item.days_remaining}d
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsCards({ analytics }) {
  if (!analytics) return null;

  const overall = analytics.overall || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <Target className="w-3 h-3 mr-1" />Success Rate
          </CardDescription>
          <CardTitle className="text-3xl text-success">{overall.success_rate?.toFixed(1) || 0}%</CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={overall.success_rate || 0} className="h-1" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <DollarSign className="w-3 h-3 mr-1" />Total At Risk
          </CardDescription>
          <CardTitle className="text-2xl">{formatCurrency(overall.total_amount_at_risk)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground">
            {overall.pending || 0} pending appeals
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <TrendingUp className="w-3 h-3 mr-1" />Recovered
          </CardDescription>
          <CardTitle className="text-2xl text-success">{formatCurrency(overall.total_recovered)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground">
            {overall.accepted || 0} accepted appeals
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <BarChart3 className="w-3 h-3 mr-1" />Total Appeals
          </CardDescription>
          <CardTitle className="text-3xl">{overall.total_appeals || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground">
            {overall.accepted || 0} won / {overall.rejected || 0} lost
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AppealsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: '', priority: '' });
  const [activeTab, setActiveTab] = useState('all');

  const { data: summary, isLoading: loadingSummary, refetch: refetchSummary } = useQuery({
    queryKey: ['appeals-summary'],
    queryFn: getAppealsQueueSummary,
  });

  const { data: appealsData, isLoading, refetch: refetchAppeals } = useQuery({
    queryKey: ['appeals', filters],
    queryFn: () => listAppeals(filters),
    refetchInterval: 10000,
  });

  const { data: analytics } = useQuery({
    queryKey: ['appeals-analytics'],
    queryFn: getAppealsAnalytics,
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/api/appeals/clear-all`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to clear appeals');
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Appeals cleared');
      refetchSummary();
      refetchAppeals();
      queryClient.invalidateQueries(['appeals']);
      queryClient.invalidateQueries(['appeals-summary']);
      queryClient.invalidateQueries(['appeals-analytics']);
    },
    onError: () => {
      toast.error('Failed to clear appeals');
    }
  });

  const handleClearAppeals = () => {
    if (window.confirm('Are you sure you want to clear all appeals? This cannot be undone.')) {
      clearMutation.mutate();
    }
  };

  const appeals = appealsData?.appeals || [];

  const handleTabChange = (value) => {
    setActiveTab(value);
    const statusMap = {
      'all': '',
      'draft': 'DRAFT',
      'review': 'PENDING_REVIEW',
      'submitted': 'SUBMITTED'
    };
    setFilters({ ...filters, status: statusMap[value] || '' });
  };

  if (loadingSummary || isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const urgencyCounts = summary?.urgency_breakdown || {};
  const statusCounts = summary?.status_breakdown || {};

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="appeals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">Appeals Management</h1>
          <p className="text-muted-foreground">AI-powered appeals workflow and tracking</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            onClick={handleClearAppeals} 
            variant="ghost" 
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            title="Clear all appeals"
            disabled={clearMutation.isPending}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button onClick={() => refetchSummary()} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      <AnalyticsCards analytics={analytics} />

      {/* Queue Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Total Active</CardDescription>
            <CardTitle className="text-3xl">{summary?.total_appeals || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">
              At Risk: {formatCurrency(summary?.total_amount_at_risk || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Critical/Overdue</CardDescription>
            <CardTitle className="text-3xl text-red-600">
              {(urgencyCounts.OVERDUE || 0) + (urgencyCounts.CRITICAL || 0)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-red-600">Require immediate attention</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Draft</CardDescription>
            <CardTitle className="text-3xl">{statusCounts.DRAFT || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">Need letter generation</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Pending Review</CardDescription>
            <CardTitle className="text-3xl text-blue-600">{statusCounts.PENDING_REVIEW || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">Awaiting approval</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Submitted</CardDescription>
            <CardTitle className="text-3xl text-purple-600">{statusCounts.SUBMITTED || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground">Waiting for response</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Appeals Table */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>Appeals Queue</CardTitle>
              <CardDescription>Active appeals sorted by deadline urgency</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList>
                  <TabsTrigger value="all">All ({summary?.total_appeals || 0})</TabsTrigger>
                  <TabsTrigger value="draft">Draft ({statusCounts.DRAFT || 0})</TabsTrigger>
                  <TabsTrigger value="review">Review ({statusCounts.PENDING_REVIEW || 0})</TabsTrigger>
                  <TabsTrigger value="submitted">Submitted ({statusCounts.SUBMITTED || 0})</TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-4">
                  {appeals.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-sm text-muted-foreground mb-4">No appeals found</p>
                      <p className="text-xs text-muted-foreground">
                        Appeals are automatically created when denials are detected in uploaded EOBs
                      </p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Claim</TableHead>
                            <TableHead>Patient</TableHead>
                            <TableHead>Payer</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Deadline</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {appeals.map((appeal) => (
                            <TableRow
                              key={appeal.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => navigate(`/appeals/${appeal.id}`)}
                              data-testid="appeal-row"
                            >
                              <TableCell className="font-mono text-xs">{appeal.claim_id?.slice(-8)}</TableCell>
                              <TableCell className="text-sm">{appeal.patient_name || 'N/A'}</TableCell>
                              <TableCell className="text-sm max-w-[150px] truncate">{appeal.payer_name}</TableCell>
                              <TableCell className="text-sm">{appeal.denial_category}</TableCell>
                              <TableCell className="text-sm font-medium">{formatCurrency(appeal.denial_amount)}</TableCell>
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <Badge className={getUrgencyClass(appeal.urgency)}>
                                    {appeal.days_remaining >= 0 ? `${appeal.days_remaining}d` : 'Overdue'}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>{getStatusBadge(appeal.status)}</TableCell>
                              <TableCell>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upcoming Deadlines */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UpcomingDeadlines deadlines={summary?.upcoming_deadlines} />
            </CardContent>
          </Card>

          {/* AI Info Card */}
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold mb-1">AI-Powered Appeals</h4>
                  <p className="text-xs text-muted-foreground">
                    CLAIRVOY uses Gemini AI to automatically generate professional appeal letters
                    based on denial categories, payer requirements, and historical success patterns.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Success by Category */}
          {analytics?.by_category && Object.keys(analytics.by_category).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Success by Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(analytics.by_category).slice(0, 5).map(([category, stats]) => (
                  <div key={category} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate max-w-[150px]">{category}</span>
                      <span className="font-medium">{stats.success_rate?.toFixed(0) || 0}%</span>
                    </div>
                    <Progress value={stats.success_rate || 0} className="h-1" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
